import { useMemo } from 'react'
import { Empty, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { currentIntlLocale } from '@/app/i18n'
import { useThemeMode } from '@/app/theme/useThemeMode'
import { formatBytes } from '@/shared/lib/format'
import type { DayUsage } from './usageApi'

/**
 * Kunlik trafik grafigi — YUBORILGAN/QABUL QILINGAN ikkita kategoriyali
 * qator (dataviz naqshi: kategoriyaviy rang FIKR TARTIBIDA belgilanadi,
 * bu yerda validatordan o'tgan standart palitraning 1- va 2-slotlari:
 * ko'k = qabul qilingan, to'q sariq = yuborilgan — qo'shni juftlik CVD
 * bo'yicha xavfsiz).
 */
const COLOR_DOWNLOAD = { light: '#2a78d6', dark: '#3987e5' }
const COLOR_UPLOAD = { light: '#eb6834', dark: '#d95926' }

function shortDay(day: string, locale: string): string {
  const d = new Date(`${day}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return day
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(d)
}

interface TooltipPayloadItem {
  name: string
  value: number
  color: string
}

export function UsageChart({ data, compact = false }: { data: DayUsage[]; compact?: boolean }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { isDark } = useThemeMode()
  const locale = currentIntlLocale()

  const downloadColor = isDark ? COLOR_DOWNLOAD.dark : COLOR_DOWNLOAD.light
  const uploadColor = isDark ? COLOR_UPLOAD.dark : COLOR_UPLOAD.light

  const chartData = useMemo(
    () =>
      data.map((d) => ({
        day: d.day,
        label: shortDay(d.day, locale),
        [t('usage.chartDownload')]: d.downloadBytes,
        [t('usage.chartUpload')]: d.uploadBytes,
      })),
    [data, locale, t],
  )

  const downloadKey = t('usage.chartDownload')
  const uploadKey = t('usage.chartUpload')

  if (data.length === 0) {
    return <Empty description={t('usage.empty')} />
  }

  return (
    <ResponsiveContainer width="100%" height={compact ? 200 : 320}>
      <BarChart data={chartData} barCategoryGap={compact ? 2 : 4} margin={{ left: 4, right: 8, top: 8 }}>
        <CartesianGrid vertical={false} stroke={token.colorBorderSecondary} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: token.colorTextSecondary }}
          interval={compact ? 6 : 3}
          tickLine={false}
          axisLine={{ stroke: token.colorBorderSecondary }}
        />
        <YAxis
          tick={{ fontSize: 11, fill: token.colorTextSecondary }}
          tickFormatter={(v: number) => formatBytes(v, 0)}
          width={56}
          tickLine={false}
          axisLine={false}
        />
        <Tooltip
          formatter={(value: number) => formatBytes(value)}
          contentStyle={{
            background: token.colorBgElevated,
            border: `1px solid ${token.colorBorderSecondary}`,
            borderRadius: token.borderRadius,
            fontSize: 12,
          }}
          labelStyle={{ color: token.colorText }}
          itemSorter={(item) => {
            const payload = item as unknown as TooltipPayloadItem
            return payload.name === downloadKey ? 0 : 1
          }}
        />
        {!compact && <Legend wrapperStyle={{ fontSize: 12 }} />}
        <Bar dataKey={downloadKey} stackId="traffic" fill={downloadColor} radius={[0, 0, 0, 0]} />
        <Bar dataKey={uploadKey} stackId="traffic" fill={uploadColor} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  )
}
